import type { VisualEditorPage, VisualEditorWidget } from '../../types.ts';
import { liveDomUpdater } from '../../services/liveDomUpdater.ts';
import type { DrawerTab, VisualEditorDrawerCallbacks } from './VisualEditorDrawerConstants.ts';

export interface DrawerEventListenersContext {
  drawerElement: HTMLElement | null;
  page: VisualEditorPage | null;
  activeTab: DrawerTab;
  setActiveTab: (tab: DrawerTab) => void;
  selectedWidgetId: string | null;
  editedValues: Record<string, unknown>;
  pendingInsertion: {
    widgetId: string;
    position: 'before' | 'after';
    displayText: string;
  } | null;
  setPendingInsertion: (
    val: { widgetId: string; position: 'before' | 'after'; displayText: string } | null
  ) => void;
  callbacks: VisualEditorDrawerCallbacks | null;
  isJustDropped: () => boolean;
  close: () => void;
  renderContent: () => void;
  showToast: (msg: string) => void;
  selectWidget: (id: string) => void;
  findWidgetRecursive: (id: string | null) => VisualEditorWidget | null;
  findParentWidget: (id: string) => VisualEditorWidget | null;
  promptDeleteConfirm: (id: string, name: string, onConfirm: () => Promise<void> | void) => void;
  promptFileDeleteConfirm: (
    fileName: string,
    url: string,
    onConfirm: () => Promise<void> | void
  ) => void;
  handleFileUpload: (file: File, fieldKey: string) => Promise<void>;
  handleFileRemove: (fieldKey: string, fileUrl: string, index: number) => Promise<void>;
  setupDragAndDrop: () => void;
  setupFlowItemsDragAndDrop: () => void;
  moveWidget: (widgetId: string, direction: number) => void;
  moveSubItem: (parentId: string, subItemId: string, direction: number) => void;
}

export function attachDrawerEventListeners(ctx: DrawerEventListenersContext): void {
  if (!ctx.drawerElement) return;

  // Close button
  ctx.drawerElement.querySelector('#ve-btn-close')?.addEventListener('click', () => ctx.close());

  // Tab buttons
  ctx.drawerElement.querySelectorAll('.ve-tab-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      let tab = (e.currentTarget as HTMLElement).dataset.tab as DrawerTab;
      const alias = (e.currentTarget as HTMLElement).dataset.alias;
      if (alias === 'blocks') tab = 'editor';
      if (alias === 'catalog') tab = 'add';
      if (tab) {
        ctx.setActiveTab(tab);
        ctx.renderContent();
      }
    });
  });

  // Form inputs live updates
  ctx.drawerElement.querySelectorAll('.ve-input, .ve-textarea, .ve-select').forEach((input) => {
    const el = input as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const key = el.dataset.key;
    if (!key || !ctx.selectedWidgetId) return;

    const triggerUpdate = () => {
      const val = el.value;
      ctx.editedValues[key] = val;
      liveDomUpdater.updateWidgetField(ctx.selectedWidgetId!, key, val);
    };

    el.addEventListener('input', triggerUpdate);
    el.addEventListener('change', triggerUpdate);
  });

  // Switch toggles live update
  ctx.drawerElement.querySelectorAll('.ve-switch input[type="checkbox"]').forEach((cb) => {
    const el = cb as HTMLInputElement;
    const key = el.dataset.key;
    if (!key || !ctx.selectedWidgetId) return;

    el.addEventListener('change', () => {
      ctx.editedValues[key] = el.checked;
      liveDomUpdater.updateWidgetField(ctx.selectedWidgetId!, key, el.checked);
    });
  });

  // Stepper buttons
  ctx.drawerElement.querySelectorAll('.ve-stepper-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const targetId = (btn as HTMLElement).dataset.target;
      if (!targetId) return;
      const input = document.getElementById(targetId) as HTMLInputElement;
      if (!input) return;

      let cur = Number(input.value) || 0;
      if (btn.classList.contains('btn-step-up')) cur += 1;
      if (btn.classList.contains('btn-step-down')) cur -= 1;

      input.value = String(cur);
      input.dispatchEvent(new Event('input'));
    });
  });

  // File / Media Dropzone handling
  ctx.drawerElement.querySelectorAll('.ve-dropzone').forEach((dz) => {
    const dropzone = dz as HTMLElement;
    const key = dropzone.dataset.key;
    if (!key) return;

    const fileInput = dropzone.querySelector<HTMLInputElement>('.ve-file-input');
    dropzone.addEventListener('click', () => fileInput?.click());

    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('dragover');
    });

    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));

    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('dragover');
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        ctx.handleFileUpload(e.dataTransfer.files[0], key);
      }
    });

    fileInput?.addEventListener('change', () => {
      if (fileInput.files && fileInput.files.length > 0) {
        ctx.handleFileUpload(fileInput.files[0], key);
      }
    });
  });

  // Attached file removal buttons
  ctx.drawerElement.querySelectorAll('.ve-btn-remove-file').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const target = btn as HTMLElement;
      const key = target.dataset.key;
      const url = target.dataset.url || '';
      const fileName = target.dataset.filename || url.split('/').pop() || 'Attached File';
      const indexStr = target.dataset.index;
      const index = indexStr !== undefined ? parseInt(indexStr, 10) : -1;

      if (!key) return;

      ctx.promptFileDeleteConfirm(fileName, url, async () => {
        await ctx.handleFileRemove(key, url, index);
      });
    });
  });

  if (ctx.activeTab === 'tree') {
    ctx.setupDragAndDrop();
  } else if (ctx.activeTab === 'editor') {
    ctx.setupFlowItemsDragAndDrop();
  }

  // Breadcrumbs parent click
  ctx.drawerElement.querySelectorAll('.ve-breadcrumb-parent').forEach((btn) => {
    btn.addEventListener('click', () => {
      const parentId = (btn as HTMLElement).dataset.parentId;
      if (parentId) {
        ctx.selectWidget(parentId);
      }
    });
  });

  // Flow items actions in editor tab
  ctx.drawerElement.querySelectorAll('.ve-flow-btn-edit').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.id;
      if (id) {
        ctx.selectWidget(id);
      }
    });
  });

  ctx.drawerElement.querySelector('#ve-btn-add-subitem')?.addEventListener('click', (e) => {
    const target = e.currentTarget as HTMLElement;
    const parentId = target.dataset.parentId;
    const subType = target.dataset.subType || 'FeatureItem';
    if (parentId) {
      ctx.callbacks?.onWidgetAdded(subType, parentId);
    }
  });

  ctx.drawerElement.querySelectorAll('.flow-btn-up').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.id;
      const parentId = (btn as HTMLElement).dataset.parentId;
      if (id && parentId) ctx.moveSubItem(parentId, id, -1);
    });
  });

  ctx.drawerElement.querySelectorAll('.flow-btn-down').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.id;
      const parentId = (btn as HTMLElement).dataset.parentId;
      if (id && parentId) ctx.moveSubItem(parentId, id, 1);
    });
  });

  ctx.drawerElement.querySelectorAll('.flow-btn-del').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.id;
      const parentId = (btn as HTMLElement).dataset.parentId;
      if (!id || !parentId) return;
      const sub = ctx.findWidgetRecursive(id);
      const name = sub?.displayText || sub?.contentType || 'Item';
      ctx.promptDeleteConfirm(id, name, () => {
        return ctx.callbacks?.onWidgetDeleted(id, parentId);
      });
    });
  });

  // Widget Tree clicks
  ctx.drawerElement.querySelectorAll('.ve-widget-tree-item').forEach((item) => {
    item.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.ve-badge-btn')) return;
      if ((e.target as HTMLElement).closest('.ve-tree-drag-handle')) return;
      if (ctx.isJustDropped()) return;

      const id = (item as HTMLElement).dataset.id;
      if (id) {
        ctx.selectWidget(id);
      }
    });
  });

  // Widget Tree move/delete
  ctx.drawerElement.querySelectorAll('.tree-btn-up').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.id;
      if (id) ctx.moveWidget(id, -1);
    });
  });

  ctx.drawerElement.querySelectorAll('.tree-btn-down').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.id;
      if (id) ctx.moveWidget(id, 1);
    });
  });

  ctx.drawerElement.querySelectorAll('.tree-btn-del').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.id;
      const parentId = (btn as HTMLElement).dataset.parentId;
      if (!id || !ctx.page) return;
      const widget = ctx.findWidgetRecursive(id);
      const name = widget?.displayText || widget?.contentType || 'Widget';
      ctx.promptDeleteConfirm(id, name, () => {
        if (parentId) {
          return ctx.callbacks?.onWidgetDeleted(id, parentId);
        } else {
          return ctx.callbacks?.onWidgetDeleted(id);
        }
      });
    });
  });

  // Insertion reset button
  ctx.drawerElement.querySelector('#ve-btn-insertion-reset')?.addEventListener('click', () => {
    ctx.setPendingInsertion(null);
    ctx.renderContent();
  });

  // Add Widget catalog card clicks
  ctx.drawerElement.querySelectorAll('.ve-catalog-card').forEach((card) => {
    card.addEventListener('click', () => {
      const type = (card as HTMLElement).dataset.type;
      if (type) {
        let insertIndex: number | undefined = undefined;
        if (ctx.pendingInsertion && ctx.page) {
          const targetIdx = ctx.page.widgets.findIndex(
            (w) => w.contentItemId === ctx.pendingInsertion?.widgetId
          );
          if (targetIdx !== -1) {
            insertIndex = ctx.pendingInsertion.position === 'before' ? targetIdx : targetIdx + 1;
          }
        }
        ctx.setPendingInsertion(null);
        ctx.callbacks?.onWidgetAdded(type, null, insertIndex);
      }
    });
  });

  // Page Settings save button
  ctx.drawerElement.querySelector('#ve-btn-save-settings')?.addEventListener('click', () => {
    const pageTitle = (document.getElementById('ve-seo-title') as HTMLInputElement)?.value || '';
    const metaDescription =
      (document.getElementById('ve-seo-desc') as HTMLTextAreaElement)?.value || '';
    const displayText = (document.getElementById('ve-page-title') as HTMLInputElement)?.value || '';
    const path = (document.getElementById('ve-page-path') as HTMLInputElement)?.value || '';

    ctx.callbacks?.onPageSettingsUpdated({ displayText, pageTitle, metaDescription, path });
  });

  // Action Footer buttons
  ctx.drawerElement.querySelector('#ve-btn-discard')?.addEventListener('click', () => {
    ctx.close();
    window.location.reload();
  });

  ctx.drawerElement.querySelector('#ve-btn-draft')?.addEventListener('click', async () => {
    if (ctx.selectedWidgetId && Object.keys(ctx.editedValues).length > 0) {
      const parent = ctx.findParentWidget(ctx.selectedWidgetId);
      if (parent) {
        await ctx.callbacks?.onWidgetUpdated(
          ctx.selectedWidgetId,
          ctx.editedValues,
          parent.contentItemId
        );
      } else {
        await ctx.callbacks?.onWidgetUpdated(ctx.selectedWidgetId, ctx.editedValues);
      }
    }
    await ctx.callbacks?.onSaveDraft();
    ctx.showToast('Draft Saved Successfully');
  });

  ctx.drawerElement.querySelector('#ve-btn-publish')?.addEventListener('click', async () => {
    if (ctx.selectedWidgetId && Object.keys(ctx.editedValues).length > 0) {
      const parent = ctx.findParentWidget(ctx.selectedWidgetId);
      if (parent) {
        await ctx.callbacks?.onWidgetUpdated(
          ctx.selectedWidgetId,
          ctx.editedValues,
          parent.contentItemId
        );
      } else {
        await ctx.callbacks?.onWidgetUpdated(ctx.selectedWidgetId, ctx.editedValues);
      }
    }
    await ctx.callbacks?.onPublish();
    ctx.showToast('Page Changes Published Live!');
  });
}
